import ProductPriceService from './ProductPriceService';
import type { Amount, CalculatedPrice, Price } from '@/modules/store/models/Price';
import type { Product } from '@/modules/store/models/Product';
import type { Promotion } from '@/modules/store/models/Promotion';

interface Sku {
  prices: Price[];
  promotions: Promotion[];
}

export interface CartPriceItem {
  quantity?: number;
  product?: Product;
  sku?: Sku;
  lineItemPrice?: CalculatedPrice;
  vatAmount?: Amount;
}

class CartPriceService {
  constructor(private readonly productPriceService = new ProductPriceService()) {}

  calculateSubtotal(items: CartPriceItem[]): CalculatedPrice | undefined {
    return this.calculateTotal(items, false);
  }

  calculateGrandTotal(items: CartPriceItem[]): CalculatedPrice | undefined {
    return this.calculateTotal(items, true);
  }

  private calculateTotal(
    items: CartPriceItem[],
    includingVat?: boolean,
  ): CalculatedPrice | undefined {
    let totalAmount = 0;
    let totalDiscountAmount = 0;

    const currencyCode = this.getCurrencyCode(items);
    if (!currencyCode) {
      return;
    }

    for (const item of items) {
      if (!item.quantity) {
        continue;
      }

      const lineItemPrice =
        item.lineItemPrice ||
        (item.sku
          ? this.productPriceService.calculateLineItemPrice(item.sku, item.quantity)
          : undefined);
      const lineItemAmount = lineItemPrice?.amount || 0;

      totalAmount += lineItemAmount;
      if (lineItemPrice?.discountedPrice) {
        totalDiscountAmount += lineItemPrice.discountedPrice.amount;
      }

      if (includingVat) {
        if (item.vatAmount) {
          totalAmount += item.vatAmount.amount;
        } else if (item.product && item.product.vatRate && item.product.vatRate > 0) {
          const vatAmount = lineItemAmount * (item.product.vatRate / 100);
          totalAmount += vatAmount;
        }
      }
    }

    const originalPrice = {
      currencyCode,
      amount: totalAmount,
    };

    return {
      ...originalPrice,
      originalPrice,
      discountedPrice:
        totalDiscountAmount > 0
          ? {
              currencyCode,
              amount: totalDiscountAmount,
            }
          : undefined,
    };
  }

  private getCurrencyCode(items: CartPriceItem[]): string | undefined {
    for (const item of items) {
      if (item.lineItemPrice?.currencyCode) {
        return item.lineItemPrice.currencyCode;
      }

      const skuCurrencyCode = item.sku?.prices?.[0]?.price.currencyCode;
      if (skuCurrencyCode) {
        return skuCurrencyCode;
      }
    }
  }
}

export default CartPriceService;
